import { Box, Group, Stack, useMantineTheme } from '@mantine/core';
import type { EntityType } from '@repo/common/enums/entityType';
import { observer } from 'mobx-react-lite';
import {
	CommentStoreContext,
	CommentStoreProvider,
} from '../../../../components/Comment';
import EditorWithComments from '../../../../components/Comment/EditorWithComments';
import FixedCommentThread from '../../../../components/Comment/FixedCommentSidebar/FixedCommentThread';
import type { CommentActionHandlers, IResourceContentProps } from '../../types';

interface ResourceCommentContentProps extends IResourceContentProps {
	commentActions: CommentActionHandlers;
}

function ResourceCommentContentInner({
	selected,
	commentActions,
}: ResourceCommentContentProps) {
	const theme = useMantineTheme();
	const { entity_type } = selected;
	const commentThreads = selected.computedMetadata.commentThreads || {};

	// Normalize IDs in metadata (strip optional 'Comment: ' prefix)
	const normalizeId = (val?: string) =>
		val ? val.replace(/^Comment:\s*/, '') : undefined;
	const rootThreadId = normalizeId(
		selected.metadata?.root_id as string | undefined
	);
	const focusedCommentId = normalizeId(
		selected.metadata?.comment_id as string | undefined
	);

	const threadComments = rootThreadId
		? commentThreads[rootThreadId]
		: undefined;

	// Deprecated: deletedOwnerName no longer used

	// topOffset will be computed via context consumer below

	if (!selected.entity) {
		return null;
	}

	return (
		<CommentStoreProvider>
			<Group
				spacing={0}
				align="flex-start"
				noWrap
				sx={{ width: '100%', position: 'relative' }}
				data-comment-wrapper
			>
				<Stack spacing={0} sx={{ flexGrow: 1 }}>
					<EditorWithComments
						entity={selected.entity}
						commentThreads={commentThreads}
						initialFocusedCommentID={rootThreadId}
						handleCreateComment={commentActions.handleCreateComment}
						handleUpdateComment={commentActions.handleUpdateComment}
						handleDeleteComment={commentActions.handleDeleteComment}
						handleEntityChange={() => async () => Promise.resolve()}
						entityType={entity_type as EntityType}
					/>
				</Stack>
				{/* Render thread via context consumer to obtain commentStore without hooks */}
				{rootThreadId ? (
					<CommentStoreContext.Consumer>
						{(store) => {
							const topOffset = store?.commentTopOffsetMap[rootThreadId] ?? 0;
							return (
								<Box
									sx={{
										maxWidth: 320,
										position: 'absolute',
										right: 0,
										top: topOffset,
										background: theme.other.getColor('fill/primary/default'),
									}}
								>
									<FixedCommentThread
										rootCommentID={rootThreadId}
										comments={threadComments || []}
										focusedId={focusedCommentId}
										onCreateComment={commentActions.handleCreateComment}
										onUpdateComment={commentActions.handleUpdateComment}
										onDeleteComment={commentActions.handleDeleteComment}
										deletedOwner={
											selected.sender
												? {
														id: selected.sender.id,
														display_name: selected.sender.display_name,
														profile_picture:
															selected.sender.profile_picture || undefined,
													}
												: null
										}
										deletedAt={selected.updated_at}
									/>
								</Box>
							);
						}}
					</CommentStoreContext.Consumer>
				) : null}
			</Group>
		</CommentStoreProvider>
	);
}

export const ResourceCommentContent = observer(ResourceCommentContentInner);
